import {
  canBeGreen,
  DATA_STATUS_LABEL,
  type DataStatus,
  type MetricValue,
  type SemaphoreResult,
  type SemaphoreState,
} from '@oxxean/types';
import { formatNumber, type ValueFormat } from './format';

/**
 * Resolución del semáforo en la capa visual.
 *
 * Regla dura: un verde solo puede nacer de `REAL` o `MANUAL` (`canBeGreen`).
 * Cuando el backend clasifica «Controlado» sobre un dato que no puede ser verde,
 * la interfaz DEGRADA el semáforo a neutro en lugar de afirmar que algo está bien.
 *
 * Nota de criterio: `docs/definicion-indicadores.md` §6.2 admite conservar el
 * color del umbral en `PARTIAL`. Aquí se adopta la lectura estricta del encargo
 * —«un semáforo verde solo puede venir de REAL o MANUAL»— porque el error que se
 * está corrigiendo (D-04) es exactamente afirmar salud sin evidencia completa.
 * Ámbar y rojo sobre datos parciales SÍ se conservan: alertar de menos es el
 * riesgo que se quiere evitar.
 */
export interface ResolvedSemaphore {
  state: SemaphoreState;
  label: string;
  /** true si el estado del dato obligó a degradar la clasificación del backend. */
  downgraded: boolean;
}

export function resolveSemaphore(
  status: DataStatus,
  semaphore: SemaphoreResult | undefined,
): ResolvedSemaphore | null {
  if (!semaphore) return null;

  if (status === 'NO_RECORDS' || status === 'NOT_AVAILABLE') {
    return { state: 'NEUTRAL', label: DATA_STATUS_LABEL[status], downgraded: true };
  }

  if (semaphore.state === 'CONTROLLED' && !canBeGreen(status)) {
    return { state: 'NEUTRAL', label: 'Sin evaluar', downgraded: true };
  }

  return { state: semaphore.state, label: semaphore.label, downgraded: false };
}

export function resolveMetricSemaphore(
  metric: MetricValue,
  semaphore: SemaphoreResult | undefined,
): ResolvedSemaphore | null {
  return resolveSemaphore(metric.status, semaphore);
}

const SEMAPHORE_TONE: Record<SemaphoreState, 'green' | 'amber' | 'red' | 'neutral'> = {
  CONTROLLED: 'green',
  WATCH: 'amber',
  CRITICAL: 'red',
  NEUTRAL: 'neutral',
};

export function semaphoreTone(state: SemaphoreState): 'green' | 'amber' | 'red' | 'neutral' {
  return SEMAPHORE_TONE[state];
}

/**
 * Texto de los umbrales vigentes, para tooltip de auditoría.
 *
 * Los números provienen SIEMPRE de `SemaphoreResult.thresholds` (DEC-A08). Este
 * módulo no conoce ningún corte: si el backend no los envía, no se muestra nada.
 */
export function thresholdHint(
  thresholds: SemaphoreResult['thresholds'],
  format: ValueFormat = 'decimal',
): string | null {
  if (!thresholds) return null;
  const green = formatNumber(thresholds.green, format);
  const yellow = formatNumber(thresholds.yellow, format);
  return thresholds.lowerIsBetter
    ? `Umbrales vigentes · Controlado < ${green} · Seguimiento < ${yellow} · Crítico ≥ ${yellow}`
    : `Umbrales vigentes · Controlado > ${green} · Seguimiento > ${yellow} · Crítico ≤ ${yellow}`;
}

/** ¿Este estado admite mostrar una cifra? */
export function hasPresentableValue(metric: MetricValue): boolean {
  if (metric.value === null) return false;
  return metric.status === 'REAL' || metric.status === 'PARTIAL' || metric.status === 'MANUAL';
}
